import { GAME_API_CACHE_TTL } from 'config/constants';
import { axios } from 'setup/axiosSetup';
import { CasinoGame } from 'types/CasinoGame';
import { readStoredUser } from 'lib/authSession';

const getGameApiBaseUrl = (): string =>
  process.env.REACT_APP_GAME_API_BACKEND_API ?? '';

function playerAuthHeaders(): Record<string, string> {
  try {
    const user = readStoredUser();
    if (!user?.access_token) return {};
    return { Authorization: `Bearer ${user.access_token}` };
  } catch {
    return {};
  }
}

const DEFAULT_CASINO_PER_PAGE = 100;

// Game pictures come from our own disk cache (nginx /img/game/, /img/live/)
// instead of the provider's hosts, which were slow to fill in a lobby.
const KEPT_PICTURES: [string, string][] = [
  ['https://gamesstatic.com/games/', '/img/game/'],
  ['https://static.turnkey.casino/games/', '/img/live/'],
];
const KEPT_PICTURE_NAME = /^[A-Za-z0-9_/-]+\.(png|jpe?g|webp|gif)$/;

export const keptGamePicture = (url: string): string => {
  for (const [from, to] of KEPT_PICTURES) {
    if (url?.startsWith(from)) {
      const name = url.slice(from.length);
      return KEPT_PICTURE_NAME.test(name) ? to + name : url;
    }
  }
  return url;
};

const withKeptPictures = <T extends { items: CasinoGame[] }>(page: T): T => ({
  ...page,
  items: (page.items ?? []).map((g) => ({
    ...g,
    image: keptGamePicture(g.image),
  })),
});

/** One page of casino catalog from the merchant API (``?page=`` on Django). */
export interface CasinoGamesPageResponse {
  items: CasinoGame[];
  page: number;
  pageCount: number;
  meta?: Record<string, unknown>;
}

/** `whole`: every game in one answer, from the list the API keeps (the
 *  provider pages 50 at a time, and the lobby grew in front of the player). */
export const getCasinoGamesPage = async (
  page: number,
  whole = false
): Promise<CasinoGamesPageResponse> => {
  const response = await axios.get(
    `${getGameApiBaseUrl()}/get_all_games_and_providers`,
    {
      params: whole ? { all: 1 } : { page, per_page: DEFAULT_CASINO_PER_PAGE },
      cache: {
        ttl: GAME_API_CACHE_TTL,
      },
    }
  );
  return withKeptPictures(response.data);
};

/** Legacy: Django aggregates every upstream page (slow). Prefer ``getCasinoGamesPage``. */
export const getAllGamesAndProviders = async (): Promise<CasinoGame[]> => {
  const response = await axios.get(
    `${getGameApiBaseUrl()}/get_all_games_and_providers`,
    {
      cache: {
        ttl: GAME_API_CACHE_TTL,
      },
    }
  );
  return response.data;
};

export const getGameUrl = async (
  gameId: string,
  playerId: number,
  playerUsername: string
): Promise<string> => {
  const response = await axios.post(
    `${getGameApiBaseUrl()}/init-casino`,
    {
      game_uuid: gameId,
      player_id: playerId,
      player_name: playerUsername,
    },
    {
      cache: false,
      headers: playerAuthHeaders(),
    }
  );
  return response.data.url as string;
};

/** Live casino: one catalog page (``?page=`` on Django). Same JSON shape as ``CasinoGamesPageResponse``. */
export interface LiveCasinoGamesPageResponse {
  items: CasinoGame[];
  page: number;
  pageCount: number;
  meta?: Record<string, unknown>;
}

/** `whole`: every game in one answer, from the list the API keeps (the
 *  provider pages 50 at a time, and the lobby grew in front of the player). */
export const getLiveCasinoGamesPage = async (
  page: number,
  whole = false
): Promise<LiveCasinoGamesPageResponse> => {
  const response = await axios.get(
    `${getGameApiBaseUrl()}/get_all_live_casino_games`,
    {
      params: whole ? { all: 1 } : { page, per_page: DEFAULT_CASINO_PER_PAGE },
      cache: {
        ttl: GAME_API_CACHE_TTL,
      },
    }
  );
  return withKeptPictures(response.data);
};

/** Legacy: Django aggregates every upstream page (slow). Prefer ``getLiveCasinoGamesPage``. */
export const getAllLiveCasinoGames = async (): Promise<CasinoGame[]> => {
  const response = await axios.get(
    `${getGameApiBaseUrl()}/get_all_live_casino_games`,
    {
      cache: {
        ttl: GAME_API_CACHE_TTL,
      },
    }
  );
  return response.data;
};

export const getLiveCasinoGameUrl = async (
  gameId: string,
  playerId: number,
  playerUsername: string
): Promise<string> => {
  const response = await axios.post(
    `${getGameApiBaseUrl()}/init-live-casino`,
    {
      game_uuid: gameId,
      player_id: playerId,
      player_name: playerUsername,
    },
    {
      cache: false,
      headers: playerAuthHeaders(),
    }
  );
  return response.data.url as string;
};

/** A game the player starred; kept with the account, on every device. */
export type CasinoFavorite = { uuid: string; kind: 'casino' | 'live' };

export const getCasinoFavorites = async (): Promise<CasinoFavorite[]> => {
  const response = await axios.get(`${getGameApiBaseUrl()}/casino_favorites`, {
    cache: false,
    headers: playerAuthHeaders(),
  });
  return response.data?.favorites ?? [];
};

/** Stars or unstars a game; answers the player's whole list. */
export const setCasinoFavorite = async (
  uuid: string,
  kind: CasinoFavorite['kind'],
  favorite: boolean
): Promise<CasinoFavorite[]> => {
  const response = await axios.post(
    `${getGameApiBaseUrl()}/casino_favorites`,
    { uuid, kind, favorite },
    { cache: false, headers: playerAuthHeaders() }
  );
  return response.data?.favorites ?? [];
};
